<template>
  <div class="square-terminal-device">
    <!-- Device Header with Name and Status -->
    <div class="device-header">
      <div class="name">
        {{ name }}
      </div>
      <div class="status-indicator">
        <div
          class="status-badge"
          :class="statusClass"
        >
          <font-awesome-icon
            :icon="statusIcon"
            class="status-icon"
            fixed-width
          />
          {{ statusText }}
        </div>
      </div>
    </div>

    <!-- Device Information Grid -->
    <div class="device-info">
      <!-- Device ID -->
      <div class="info-section device-id">
        <div class="info-label">
          Device ID
        </div>
        <div class="info-value device-id-value">
          {{ deviceId }}
        </div>
        <div class="info-detail">
          Unique identifier
        </div>
      </div>

      <!-- Device Model and Version -->
      <div class="info-section device-model">
        <div class="info-label">
          Device
        </div>
        <div class="info-value">
          {{ deviceModel }}
        </div>
        <div class="info-detail">
          Version {{ deviceVersion }}
        </div>
      </div>

      <!-- Location Information -->
      <div
        v-if="sessionLocation"
        class="info-section location-info"
      >
        <div class="info-label">
          Location
        </div>
        <div class="info-value">
          {{ sessionLocation.display_name || sessionLocation.name }}
        </div>
        <div class="info-detail">
          {{ sessionLocation.address && sessionLocation.address.locality || 'Unknown' }}
        </div>
      </div>

      <!-- Battery Status (if available) -->
      <div
        v-if="batteryInfo"
        class="info-section battery-status"
      >
        <div class="info-label">
          Battery
        </div>
        <div class="info-value">
          <font-awesome-icon
            :icon="batteryIcon"
            :class="batteryClass"
            fixed-width
          />
          {{ batteryInfo.visible_percent }}%
        </div>
        <div class="info-detail">
          {{ batteryStatusText }}
        </div>
      </div>

      <!-- Network Status -->
      <div
        v-if="networkInfo"
        class="info-section network-status"
      >
        <div class="info-label">
          Network
        </div>
        <div class="info-value">
          <font-awesome-icon
            :icon="networkIcon"
            :class="networkClass"
            fixed-width
          />
          {{ networkType }}
        </div>
        <div
          v-if="wifiInfo"
          class="info-detail"
        >
          {{ wifiInfo.ssid }} ({{ signalStrengthText }})
        </div>
        <div
          v-else-if="ethernetInfo && ethernetInfo.active"
          class="info-detail"
        >
          Ethernet Connected
        </div>
      </div>

      <!-- Application Version -->
      <div
        v-if="applicationInfo"
        class="info-section app-version"
      >
        <div class="info-label">
          Terminal App
        </div>
        <div class="info-value">
          v{{ applicationInfo.version }}
        </div>
        <div class="info-detail">
          {{ applicationInfo.application_type }}
        </div>
      </div>

      <!-- Card Reader Version -->
      <div
        v-if="cardReaderInfo"
        class="info-section card-reader"
      >
        <div class="info-label">
          Card Reader
        </div>
        <div class="info-value">
          v{{ cardReaderInfo.version }}
        </div>
        <div class="info-detail">
          Ready
        </div>
      </div>
    </div>

    <!-- Remove Device Button -->
    <div
      v-if="allowRemoveButton"
      class="remove-device-button"
    >
      <b-modal
        :id="`confirm-dialog--${device.id}`"
        :title="`Remove Device: ${name}`"
        static
        @ok="removeTerminalDevice"
      >
        <p><strong>Are you sure that you wish to remove this terminal device?</strong></p>
        <p>This action cannot be undone.</p>
      </b-modal>
      <b-button
        variant="outline-danger"
        size="sm"
        :disabled="processing"
        @click="$bvModal.show(`confirm-dialog--${device.id}`)"
      >
        <font-awesome-icon icon="trash" />
        Remove
        <b-spinner
          v-if="processing"
          small
        />
      </b-button>
    </div>
  </div>
</template>

<script>

export default {
  name: 'square-terminal-device-details',
  props: {
    device: {
      type: Object,
      required: true
    },
    remove: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function() {
    return {
      processing: false
    }
  },
  computed: {
    // Device ID (clean, without prefix)
    deviceId: function() {
      const id = this.device.id || ''
      // Remove "device:" prefix if present
      return id.replace(/^device:/, '')
    },

    allowRemoveButton: function() {
      return this.remove && false
    },
    name: function() {
      return (this.device.attributes && this.device.attributes.name) || this.device.label || 'Unknown Device'
    },

    // Device Status
    deviceStatus: function() {
      return (this.device.status && this.device.status.category) || 'UNKNOWN'
    },

    statusClass: function() {
      switch (this.deviceStatus) {
      case 'OFFLINE': return 'status-offline'
      case 'ONLINE': return 'status-online'
      case 'NEEDS_ATTENTION': return 'status-warning'
      default: return 'status-unknown'
      }
    },

    statusIcon: function() {
      switch (this.deviceStatus) {
      case 'OFFLINE': return 'circle-xmark'
      case 'ONLINE': return 'circle-check'
      case 'NEEDS_ATTENTION': return 'triangle-exclamation'
      default: return 'circle-question'
      }
    },

    statusText: function() {
      switch (this.deviceStatus) {
      case 'OFFLINE': return 'Offline'
      case 'ONLINE': return 'Online'
      case 'NEEDS_ATTENTION': return 'Needs Attention'
      default: return 'Unknown'
      }
    },

    // Device Model and Version
    deviceModel: function() {
      const manufacturer = (this.device.attributes && this.device.attributes.manufacturer) || ''
      const model = (this.device.attributes && this.device.attributes.model) || ''
      return manufacturer && model ? `${manufacturer} ${model}` : 'Unknown Model'
    },

    deviceVersion: function() {
      return (this.device.attributes && this.device.attributes.version) || 'Unknown'
    },

    // Location Information
    sessionLocation: function() {
      const sessionLocationId = this.applicationInfo && this.applicationInfo.session_location
      if (sessionLocationId) {
        return this.$store.state.square.locations && this.$store.state.square.locations.find(loc => loc.id === sessionLocationId)
      }
      return null
    },

    // Component Information Getters
    components: function() {
      return this.device.components || []
    },

    applicationInfo: function() {
      const appComponent = this.components.find(c => c.type === 'APPLICATION')
      return (appComponent && appComponent.application_details) || null
    },

    cardReaderInfo: function() {
      const readerComponent = this.components.find(c => c.type === 'CARD_READER')
      return (readerComponent && readerComponent.card_reader_details) || null
    },

    batteryInfo: function() {
      const batteryComponent = this.components.find(c => c.type === 'BATTERY')
      return (batteryComponent && batteryComponent.battery_details) || null
    },

    wifiInfo: function() {
      const wifiComponent = this.components.find(c => c.type === 'WIFI')
      return (wifiComponent && wifiComponent.wifi_details) || null
    },

    ethernetInfo: function() {
      const ethernetComponent = this.components.find(c => c.type === 'ETHERNET')
      return (ethernetComponent && ethernetComponent.ethernet_details) || null
    },

    // Battery Status
    batteryIcon: function() {
      if (!this.batteryInfo) return 'battery-empty'
      const percent = this.batteryInfo.visible_percent
      if (percent >= 75) return 'battery-full'
      if (percent >= 50) return 'battery-three-quarters'
      if (percent >= 25) return 'battery-half'
      if (percent >= 10) return 'battery-quarter'
      return 'battery-empty'
    },

    batteryClass: function() {
      if (!this.batteryInfo) return 'battery-unknown'
      const percent = this.batteryInfo.visible_percent
      if (percent <= 20) return 'battery-low'
      if (percent <= 50) return 'battery-medium'
      return 'battery-good'
    },

    batteryStatusText: function() {
      if (!this.batteryInfo) return 'Unknown'
      const externalPower = this.batteryInfo.external_power
      if (externalPower === 'AVAILABLE_CHARGING') return 'Charging'
      if (externalPower === 'AVAILABLE_NOT_CHARGING') return 'Plugged In'
      return 'On Battery'
    },

    // Network Status
    networkInfo: function() {
      return this.wifiInfo || this.ethernetInfo
    },

    networkType: function() {
      if (this.wifiInfo && this.wifiInfo.active) return 'Wi-Fi'
      if (this.ethernetInfo && this.ethernetInfo.active) return 'Ethernet'
      return 'Disconnected'
    },

    networkIcon: function() {
      if (this.wifiInfo && this.wifiInfo.active) return 'wifi'
      if (this.ethernetInfo && this.ethernetInfo.active) return 'ethernet'
      return 'xmark'
    },

    networkClass: function() {
      if ((this.wifiInfo && this.wifiInfo.active) || (this.ethernetInfo && this.ethernetInfo.active)) return 'network-connected'
      return 'network-disconnected'
    },

    signalStrengthText: function() {
      if (!this.wifiInfo || !this.wifiInfo.signal_strength) return 'Unknown Signal'
      const strength = this.wifiInfo.signal_strength.value
      if (strength >= 0.8) return 'Excellent Signal'
      if (strength >= 0.6) return 'Good Signal'
      if (strength >= 0.4) return 'Fair Signal'
      if (strength >= 0.2) return 'Poor Signal'
      return 'Very Poor Signal'
    },

    // Legacy support
    location: function() {
      return this.$store.state.square.terminalLocations && this.$store.state.square.terminalLocations.find(loc => loc.id === this.device.location)
    }
  },
  methods: {
    removeTerminalDevice: function() {
      this.processing = true
      this.$store.dispatch('square/removeTerminalDevice', this.device.id)
        .then(result => {
          this.$emit('removed', this.device)
        })
        .catch(err => {
          this.$emit('error', { device: this.device, error: err })
        })
        .finally(_ => {
          this.processing = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
  .square-terminal-device {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 16px;
    height: 100%;
    background-color: #ffffff;
    display: flex;
    flex-direction: column;
    position: relative;

    .device-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 16px;

      .name {
        font-weight: bold;
        font-size: 1.2rem;
        color: #333;
        flex: 1;
        line-height: 1.3;
      }

      .status-indicator {
        flex-shrink: 0;
        margin-left: 12px;
      }
    }

    .status-badge {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 4px 8px;
      border-radius: 12px;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;

      &.status-online {
        background-color: #d4edda;
        color: #155724;
        border: 1px solid #c3e6cb;
      }

      &.status-offline {
        background-color: #f8d7da;
        color: #721c24;
        border: 1px solid #f5c6cb;
      }

      &.status-warning {
        background-color: #fff3cd;
        color: #856404;
        border: 1px solid #ffeaa7;
      }

      &.status-unknown {
        background-color: #f8f9fa;
        color: #6c757d;
        border: 1px solid #dee2e6;
      }
    }

    .device-info {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
      gap: 12px;
      flex: 1;
      margin-bottom: 16px;

      .info-section {
        display: flex;
        flex-direction: column;
        min-height: 60px;

        .info-label {
          font-size: 0.75rem;
          font-weight: 600;
          color: #6c757d;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }

        .info-value {
          font-weight: 600;
          color: #333;
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 2px;

          &.device-id-value {
            font-family: 'Courier New', monospace;
            font-size: 1.1rem;
            letter-spacing: 1px;
            color: #007bff;
          }
        }

        .info-detail {
          font-size: 0.8rem;
          color: #6c757d;
          line-height: 1.2;
        }
      }
    }

    // Battery status colors
    .battery-good {
      color: #28a745;
    }

    .battery-medium {
      color: #ffc107;
    }

    .battery-low {
      color: #dc3545;
    }

    .battery-unknown {
      color: #6c757d;
    }

    // Network status colors
    .network-connected {
      color: #28a745;
    }

    .network-disconnected {
      color: #dc3545;
    }

    .remove-device-button {
      display: flex;
      justify-content: flex-end;
      margin-top: auto;
      padding-top: 12px;
      border-top: 1px solid #e9ecef;

      .btn {
        display: flex;
        align-items: center;
        gap: 6px;
      }
    }

    // Responsive adjustments
    @media (max-width: 576px) {
      .device-info {
        grid-template-columns: 1fr;
      }

      .device-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;

        .status-indicator {
          margin-left: 0;
        }
      }
    }
  }
</style>
